import { FC, useEffect } from 'react';
import { Comm100Api, Comm100ChatProps, WindowWithComm100 } from './types';

const insertComm100Script = (baseUrl: string, siteId: number): void => {
  const script = document.createElement('script');
  const firstScriptTag = document.getElementsByTagName('script')[0];

  script.type = 'text/javascript';
  script.async = true;
  script.src = `${baseUrl}${siteId}`;

  if (firstScriptTag?.parentNode) {
    firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
  } else {
    document.head.appendChild(script);
  }
};

const COMM100_PRIMARY_URL = 'https://vue.comm100.com/livechat.ashx?siteId=';
const COMM100_STANDBY_URL = 'https://standby.comm100vue.com/livechat.ashx?siteId=';

const hasScriptBySrc = (src: string): boolean => Boolean(document.querySelector(`script[src="${src}"]`));

const getActiveOpenChatHandler = (windowWithComm100: WindowWithComm100): (() => void) | undefined => {
  const activeKey = windowWithComm100.__comm100ActiveOpenChatHandlerKey;
  const activeHandler = activeKey ? windowWithComm100.__comm100OpenChatHandlers?.[activeKey] : undefined;
  if (activeHandler) {
    return activeHandler;
  }

  const handlers = windowWithComm100.__comm100OpenChatHandlers
    ? Object.values(windowWithComm100.__comm100OpenChatHandlers)
    : [];
  return handlers[0];
};

const ensureGlobalOpenChatDispatcher = (windowWithComm100: WindowWithComm100): void => {
  if (windowWithComm100.openComm100Chat) {
    return;
  }

  windowWithComm100.openComm100Chat = () => {
    const activeHandler = getActiveOpenChatHandler(windowWithComm100);
    activeHandler?.();
  };
};

const cleanupOpenChatRegistration = (windowWithComm100: WindowWithComm100, buttonDivId: string): void => {
  if (windowWithComm100.__comm100OpenChatHandlers) {
    delete windowWithComm100.__comm100OpenChatHandlers[buttonDivId];
  }

  if (windowWithComm100.__comm100ActiveOpenChatHandlerKey === buttonDivId) {
    windowWithComm100.__comm100ActiveOpenChatHandlerKey = windowWithComm100.__comm100OpenChatHandlers
      ? Object.keys(windowWithComm100.__comm100OpenChatHandlers)[0]
      : undefined;
  }

  windowWithComm100.__comm100OpenChatRefCount = Math.max(0, (windowWithComm100.__comm100OpenChatRefCount || 1) - 1);

  if (windowWithComm100.__comm100OpenChatRefCount !== 0) {
    return;
  }

  delete windowWithComm100.__comm100OpenChatHandlers;
  delete windowWithComm100.__comm100ActiveOpenChatHandlerKey;
  delete windowWithComm100.openComm100Chat;
};

const registerOpenChat = (windowWithComm100: WindowWithComm100, comm100: Comm100Api, buttonDivId: string): void => {
  const wasRegistered = Boolean(windowWithComm100.__comm100OpenChatHandlers?.[buttonDivId]);

  const handler = (): void => {
    if (typeof comm100.do === 'function') {
      comm100.do('livechat.button.click');
      return;
    }

    const chatContainer = document.getElementById(buttonDivId);
    const triggerEl = chatContainer?.querySelector('a, button, [role="button"]') as HTMLElement | null;
    triggerEl?.click();
  };

  windowWithComm100.__comm100OpenChatHandlers = windowWithComm100.__comm100OpenChatHandlers || {};
  windowWithComm100.__comm100OpenChatHandlers[buttonDivId] = handler;
  windowWithComm100.__comm100ActiveOpenChatHandlerKey = buttonDivId;
  ensureGlobalOpenChatDispatcher(windowWithComm100);

  if (!wasRegistered) {
    windowWithComm100.__comm100OpenChatRefCount = (windowWithComm100.__comm100OpenChatRefCount || 0) + 1;
  }
};

const Comm100Chat: FC<Comm100ChatProps> = ({ settings }) => {
  const siteId = settings.key.siteId;
  const codePlan = settings.key.codePlan;

  const buttonDivId = `comm100-button-${codePlan}`;

  useEffect(() => {
    const windowWithComm100 = window as WindowWithComm100;
    windowWithComm100.Comm100API = windowWithComm100.Comm100API || {};
    const comm100 = windowWithComm100.Comm100API;

    comm100.chat_buttons = comm100.chat_buttons || [];
    const existingButtonIndex = comm100.chat_buttons.findIndex((item) => item.div_id === buttonDivId);
    const nextButton = {
      code_plan: codePlan,
      div_id: buttonDivId,
    };

    if (existingButtonIndex === -1) {
      comm100.chat_buttons.push(nextButton);
    } else if (comm100.chat_buttons[existingButtonIndex].code_plan !== codePlan) {
      comm100.chat_buttons[existingButtonIndex] = nextButton;
    }

    comm100.site_id = siteId;
    comm100.main_code_plan = codePlan;

    registerOpenChat(windowWithComm100, comm100, buttonDivId);

    const primarySrc = `${COMM100_PRIMARY_URL}${siteId}`;
    const standbySrc = `${COMM100_STANDBY_URL}${siteId}`;

    if (!hasScriptBySrc(primarySrc)) {
      insertComm100Script(COMM100_PRIMARY_URL, siteId);
    }

    const timeoutId = window.setTimeout(() => {
      if (!comm100.loaded && !hasScriptBySrc(standbySrc)) {
        insertComm100Script(COMM100_STANDBY_URL, siteId);
      }
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
      if (comm100.chat_buttons) {
        comm100.chat_buttons = comm100.chat_buttons.filter((item) => item.div_id !== buttonDivId);
      }
      cleanupOpenChatRegistration(windowWithComm100, buttonDivId);
    };
  }, [buttonDivId, codePlan, siteId]);

  return <div id={buttonDivId} />;
};

export default Comm100Chat;
